import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { auth as firebaseAuth, db } from '../lib/firebase';
import { useOrderedCol, useProgress, useDoc } from '../lib/hooks';
import { useAuthCtx } from '../lib/auth-context';
import { useCurrentCourse } from '../lib/course';
import type { ChapterDoc, CourseDoc, ResourceDoc, SectionDoc } from '../lib/data-model';
import { Loading, ErrorBlock, EmptyState, ProgressBar, Modal, toast } from '../components/ui';
import { KIND_LABEL } from './public-helpers/misc';
import { restartTour } from '../components/TutorialTour';
import {
  buildHeatGrid, buildWeekBars, fmtAvgPerWeek, fmtHm, readActivity, readDaySeconds, whenLabel,
  type HeatCell
} from '../lib/study';

export default function ProfilePage() {
  const auth = useAuthCtx();
  const navigate = useNavigate();
  const progress = useProgress(auth.isSignedIn);
  const [courseId] = useCurrentCourse();
  const uDoc = useDoc<{ name?: string }>('users', auth.user?.uid ?? '', auth.isSignedIn);
  const [editName, setEditName] = useState(false);
  const [draft, setDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Whole-collection reads + client-side filters: composite indexes are not deployed.
  const courses = useOrderedCol<CourseDoc>('courses');
  const allChapters = useOrderedCol<ChapterDoc>('chapters');
  const allSections = useOrderedCol<SectionDoc>('sections');
  const allRes = useOrderedCol<ResourceDoc>('resources');

  const chapters = {
    data: allChapters.data.filter(c => c.courseId === courseId),
    loading: allChapters.loading,
    error: allChapters.error
  };
  const courseRes = {
    data: allRes.data.filter(r => r.courseId === courseId),
    loading: allRes.loading,
    error: allRes.error
  };

  const course = courses.data.find(c => c.id === courseId) ?? courses.data[0];

  const sectionsByChapter = useMemo(() => {
    const m = new Map<string, Array<SectionDoc & { id: string }>>();
    for (const s of allSections.data) {
      if (s.hidden) continue;
      const arr = m.get(s.chapterId) ?? [];
      arr.push(s);
      m.set(s.chapterId, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.order - b.order);
    return m;
  }, [allSections.data]);

  const visibleChapters = useMemo(
    () => chapters.data.filter(c => !c.hidden).sort((a, b) => a.order - b.order),
    [chapters.data]
  );

  const chapterStats = useMemo(() => {
    return visibleChapters.map(ch => {
      const secs = sectionsByChapter.get(ch.id) ?? [];
      const secIds = new Set(secs.map(s => s.id));
      const ids = courseRes.data.filter(r => !r.hidden && secIds.has(r.sectionId)).map(r => r.id);
      const d = ids.filter(id => progress.done.has(id)).length;
      return { ch, sections: secs, ids, d, n: ids.length, pct: ids.length ? Math.round((d / ids.length) * 100) : 0 };
    });
  }, [visibleChapters, sectionsByChapter, courseRes.data, progress.done]);

  const courseIds = useMemo(() => courseRes.data.filter(r => !r.hidden).map(r => r.id), [courseRes.data]);
  const courseDoneN = courseIds.filter(id => progress.done.has(id)).length;
  const coursePct = courseIds.length ? Math.round((courseDoneN / courseIds.length) * 100) : 0;

  const mix = useMemo(() => {
    const kinds = ['video', 'pdf', 'file', 'link'] as const;
    return kinds.map(kind => {
      const ids = courseRes.data.filter(r => !r.hidden && r.kind === kind).map(r => r.id);
      const d = ids.filter(id => progress.done.has(id)).length;
      return { kind, label: kind === 'pdf' ? 'Notes & PDFs' : kind === 'link' ? 'Links' : kind === 'video' ? 'Videos' : 'Files', d, n: ids.length, pct: ids.length ? Math.round((d / ids.length) * 100) : 0 };
    });
  }, [courseRes.data, progress.done]);

  const upNext = useMemo(() => {
    const secMap = new Map<string, { order: number; chapterId: string }>();
    for (const [chId, secs] of sectionsByChapter) {
      for (const s of secs) secMap.set(s.id, { order: s.order, chapterId: chId });
    }
    const chOrder = new Map(visibleChapters.map(ch => [ch.id, ch.order]));
    const ordered = courseRes.data
      .filter(r => !r.hidden && secMap.has(r.sectionId))
      .sort((a, b) => {
        const sa = secMap.get(a.sectionId)!;
        const sb = secMap.get(b.sectionId)!;
        const ka = (chOrder.get(sa.chapterId) ?? 999) * 1e12 + sa.order * 1e6 + a.order;
        const kb = (chOrder.get(sb.chapterId) ?? 999) * 1e12 + sb.order * 1e6 + b.order;
        return ka - kb;
      });
    return ordered.find(r => !progress.done.has(r.id)) ?? null;
  }, [sectionsByChapter, visibleChapters, courseRes.data, progress.done]);

  const heat = useMemo(() => buildHeatGrid(readDaySeconds()), [progress.minutesRev, progress.done]);
  const weekBars = useMemo(() => buildWeekBars(readDaySeconds()), [progress.minutesRev]);
  const weekTotalMin = weekBars.reduce((n, b) => n + b.minutes, 0);
  const liveBars = weekBars.filter(b => !b.future);
  const wMax = Math.max(1, ...liveBars.map(b => b.minutes));
  const bestMin = liveBars.reduce((m, b) => Math.max(m, b.minutes), 0);
  let lastLiveIdx = -1;
  weekBars.forEach((b, i) => {
    if (!b.future) lastLiveIdx = i;
  });
  const bestThisWeek = bestMin > 0 && weekBars.map(b => b.minutes).lastIndexOf(bestMin) === lastLiveIdx;
  const events = useMemo(() => readActivity(), [progress.minutesRev, progress.done]);

  if (!auth.isSignedIn) {
    return (
      <div className="page-anim">
        <EmptyState
          glyph="✦"
          title="Sign in to sync your progress"
          body="Progress is saved on this device for now — sign in with Google to keep it across every device and unlock streaks and XP."
          action={
            <button className="btn btn-primary btn-lg" onClick={() => void auth.signInWithGoogle()}>
              Sign in with Google
            </button>
          }
        />
      </div>
    );
  }

  if (chapters.error || courses.error || courseRes.error) {
    return <ErrorBlock message={chapters.error ?? courses.error ?? courseRes.error ?? 'Failed to load.'} />;
  }

  if (chapters.loading || courseRes.loading) {
    return <Loading label="Loading your profile…" />;
  }

  const name = uDoc.data?.name || auth.user?.name || auth.user?.email || 'Student';

  const saveName = async () => {
    const fu = firebaseAuth.currentUser;
    const v = draft.trim();
    if (!fu || !v) return;
    setSavingName(true);
    try {
      await setDoc(doc(db, 'users', fu.uid), { email: fu.email ?? '', name: v }, { merge: true });
      toast('Name updated', 'ok');
      setEditName(false);
    } catch (e) {
      toast(`Could not save name: ${e instanceof Error ? e.message : String(e)}`, 'err');
    } finally {
      setSavingName(false);
    }
  };

  const deleteAccount = async () => {
    const fu = firebaseAuth.currentUser;
    if (!fu) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'users', fu.uid));
    } catch {
      /* doc may not exist */
    }
    try {
      await deleteUser(fu);
      for (const k of ['hub.done.v1', 'hub.watched.v1', 'hub.active.v1', 'hub.minutes.v1', 'hub.activity.v1']) {
        localStorage.removeItem(k);
      }
      window.location.href = '/';
    } catch (e) {
      setDeleting(false);
      setDelOpen(false);
      toast(
        `Could not delete the account${e instanceof Error ? ` (${e.message})` : ''} — if it says recent sign-in, sign out, sign back in and retry.`,
        'err'
      );
    }
  };
  const initials = name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const upNextInfo = upNext
    ? (() => {
        const sec = allSections.data.find(s => s.id === upNext.sectionId);
        const ch = sec ? chapters.data.find(c => c.id === sec.chapterId) : undefined;
        return { sec, ch };
      })()
    : { sec: undefined, ch: undefined };

  return (
    <div className="page-anim">
      {/* identity */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'center', marginBottom: 32 }}>
        <div
          aria-hidden
          style={{
            width: 76, height: 76, borderRadius: 24, background: 'var(--t-plum)', color: 'var(--c-plum)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-serif)', fontSize: 30, fontWeight: 600
          }}
        >
          {initials || '?'}
        </div>
        <div style={{ flex: '1 1 260px' }}>
          <h1 className="page-title" style={{ marginBottom: 6, fontSize: 'clamp(26px,3.6vw,34px)' }}>{name}</h1>
          <div className="muted" style={{ fontSize: 15 }}>
            {course ? `${course.code} · ${course.name}` : 'No course selected'}
          </div>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/')}>
          Switch course
        </button>
        <button className="btn btn-outline" onClick={() => void auth.logout()}>
          Sign out
        </button>
      </div>

      {/* stats */}
      <div className="grid-stats" style={{ marginBottom: 34 }}>
        <StatTile value={`${coursePct}%`} label="course complete" tint="var(--t-green)" line="var(--t-green-line)" fg="var(--c-green)" />
        <StatTile value={`${progress.xp}`} label="XP earned" tint="var(--t-plum)" line="var(--t-plum-line)" fg="var(--c-plum)" />
        <StatTile value={`${progress.streak}d`} label="study streak" tint="var(--t-amber)" line="var(--t-amber-line)" fg="var(--c-amber)" />
        <StatTile value={`${courseDoneN}/${courseIds.length}`} label="resources finished" tint="var(--t-blue)" line="var(--line)" fg="var(--c-blue)" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <div className="panel" style={{ flex: '1 1 420px', padding: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>
            <span className="tick" aria-hidden />
            <span>Chapter progress</span>
          </div>
          {chapterStats.length === 0 && <p className="muted">No chapters yet.</p>}
          {chapterStats.map(({ ch, sections, d, n, pct }) => {
            return (
              <Link
                key={ch.id}
                to={`/ch/${ch.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: '1px solid var(--surface2)', color: 'inherit', textDecoration: 'none' }}
              >
                <span className="mono muted" style={{ fontSize: 11.5, width: 26 }}>
                  {ch.num.padStart(2, '0')}
                </span>
                <span style={{ flex: '1 1 150px', minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14.5, fontWeight: 500 }}>{ch.title}</span>
                  <span className="mono muted" style={{ display: 'block', fontSize: 10.5, marginTop: 3 }}>
                    {d}/{n} resources · {sections.length} {sections.length === 1 ? 'section' : 'sections'}
                  </span>
                </span>
                <span style={{ flex: '0 1 120px' }}>
                  <span style={{ display: 'block', height: 7, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', borderRadius: 99, background: 'var(--amber)', width: `${pct}%`, transition: 'width .5s ease' }} />
                  </span>
                </span>
                <span className="mono muted" style={{ fontSize: 12.5, width: 38, textAlign: 'right' }}>{pct}%</span>
              </Link>
            );
          })}

          <div style={{ marginTop: 24, paddingTop: 22, borderTop: '1px solid var(--surface2)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
              <div className="eyebrow">
                <span className="tick" aria-hidden />
                <span>Study time</span>
              </div>
              <div className="mono muted" style={{ fontSize: 11.5 }}>{fmtHm(weekTotalMin * 60)} total</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 96 }}>
              {weekBars.map((w, i) => {
                const bg = w.future ? 'var(--surface2)' : i === weekBars.length - 1 ? '#2FBF6D' : i >= weekBars.length - 3 ? '#8FD9B0' : '#DCEEE4';
                const h = w.future ? 0 : w.minutes === 0 ? 4 : Math.max(6, Math.round((w.minutes / wMax) * 100));
                return (
                  <div key={i} title={`${w.label} · ${w.minutes} min`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                    <div
                      style={{
                        height: `${h}%`,
                        minHeight: w.future ? 0 : 4,
                        borderRadius: '5px 5px 3px 3px',
                        background: bg,
                        transformOrigin: 'bottom',
                        animation: `growY .8s ${0.03 * i}s ease both`
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink3)', marginTop: 8 }}>
              <span>12w ago</span>
              <span>this week</span>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 13, color: 'var(--ink2)', flexWrap: 'wrap' }}>
              <span>Avg {fmtAvgPerWeek(weekBars)} / week</span>
              {bestMin > 0 && (
                <span style={{ color: 'var(--c-green2)' }}>
                  Best {bestMin} min{bestThisWeek ? ' · this week' : ''}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginTop: 24, paddingTop: 22, borderTop: '1px solid var(--surface2)' }}>
            <div className="eyebrow" style={{ marginBottom: 15 }}>
              <span className="tick" aria-hidden />
              <span>Resource mix</span>
            </div>
            {mix.map(m => (
              <div style={{ marginBottom: 13 }} key={m.kind}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: 'var(--ink2)', marginBottom: 6 }}>
                  <span>{m.label}</span>
                  <span className="mono muted" style={{ fontSize: 12 }}>{m.d}/{m.n}</span>
                </div>
                <ProgressBar pct={m.pct} label={`${m.label} completed`} />
              </div>
            ))}
          </div>
        </div>

        <div className="panel" style={{ flex: '1 1 300px', padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
            <div className="eyebrow">
              <span className="tick" aria-hidden />
              <span>Study heatmap</span>
            </div>
            <div className="mono muted" style={{ fontSize: 11.5 }}>last 12 weeks</div>
          </div>
          <StudyHeat weeks={heat.weeks} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 16 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink2)' }}>{heat.activeDays} active days</div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span className="mono muted" style={{ fontSize: 10.5 }}>less</span>
              {HEAT_LEVELS.map((bg, i) => (
                <span key={i} style={{ width: 11, height: 11, borderRadius: 3, background: bg, display: 'block' }} />
              ))}
              <span className="mono muted" style={{ fontSize: 10.5 }}>more</span>
            </div>
          </div>

          <div style={{ marginTop: 24, paddingTop: 22, borderTop: '1px solid var(--surface2)' }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              <span className="tick" aria-hidden />
              <span>Up next</span>
            </div>
          {upNext ? (
            <Link
              to={`/res/${upNext.id}`}
              style={{
                display: 'flex', flexDirection: 'column', gap: 7, textAlign: 'left',
                background: 'var(--t-green-soft)', border: '1px solid var(--t-green-line)',
                borderRadius: 16, padding: '16px 18px', color: 'inherit', textDecoration: 'none'
              }}
            >
              <span className="mono" style={{ fontSize: 10.5, letterSpacing: '.06em', color: 'var(--c-green2)' }}>
                {upNextInfo.ch ? `CH ${upNextInfo.ch.num} · ${upNextInfo.sec?.title ?? ''}` : 'Next resource'}
              </span>
              <span style={{ fontSize: 15.5, fontWeight: 600, letterSpacing: '-.01em' }}>{upNext.title}</span>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="muted" style={{ fontSize: 12.5 }}>{KIND_LABEL[upNext.kind]}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-green2)' }}>Continue →</span>
              </span>
            </Link>
          ) : (
            <p className="muted" style={{ fontSize: 14 }}>
              Everything in this course is complete. Nicely done — revisit any chapter to review.
            </p>
          )}
          </div>

          <div style={{ marginTop: 24, paddingTop: 22, borderTop: '1px solid var(--surface2)' }}>
            <div className="eyebrow" style={{ marginBottom: 13 }}>
              <span className="tick" aria-hidden />
              <span>Recent activity</span>
            </div>
            {events.length === 0 ? (
              <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
                Your activity log will appear here as you complete resources, watch lectures and work through chapters.
              </p>
            ) : (
              events.map((e, i) => (
                <div key={`${e.ts}-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid var(--surface2)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: e.tint, marginTop: 6, flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, color: 'var(--ink2)', lineHeight: 1.4 }}>{e.what}</span>
                    <span className="mono muted" style={{ display: 'block', fontSize: 10.5, marginTop: 3 }}>{whenLabel(e.ts)}</span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* account settings */}
      <div className="panel" style={{ padding: 24, marginTop: 26 }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          <span className="tick" aria-hidden />
          <span>Account</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: '1 1 280px' }}>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>Display name</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Shown on your profile and progress. {name} · {auth.user?.email}
            </div>
          </div>
          {editName ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="onb-input"
                value={draft}
                maxLength={60}
                placeholder="Your name"
                onChange={e => setDraft(e.target.value)}
                style={{ flex: '1 1 200px', maxWidth: 320, fontSize: 15, padding: '10px 14px' }}
                autoFocus
              />
              <button className="btn btn-dark btn-sm" disabled={savingName || !draft.trim()} onClick={() => void saveName()}>
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setEditName(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button className="btn btn-outline btn-sm" onClick={() => { setDraft(name); setEditName(true); }}>
              Edit name
            </button>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 600 }}>The 20-second tour</h3>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)' }}>Replay the welcome tutorial and see the highlights again.</p>
        </div>
        <button className="btn btn-outline" onClick={restartTour}>
          Show the tutorial
        </button>
      </div>

      <div className="danger-zone" style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="danger-copy">
          <h3>Delete account</h3>
          <p>Removes your profile and saved progress from our servers and unlinks your Google sign-in. Course content stays public. This cannot be undone from the site.</p>
        </div>
        <button className="btn-danger" onClick={() => setDelOpen(true)}>
          Delete account…
        </button>
      </div>

      {delOpen && (
        <Modal title="Delete your account?" onClose={() => !deleting && setDelOpen(false)}>
          <p style={{ color: 'var(--ink2)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 10px' }}>
            Your display name, completion progress, watched videos and streak will be erased from this
            site and your Google sign-in will be unlinked.
          </p>
          <p style={{ color: 'var(--ink2)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 18px' }}>
            Your Google account itself is untouched (you can manage it at myaccount.google.com). You can
            sign in again later — but that creates a brand-new blank profile.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn btn-outline" disabled={deleting} onClick={() => setDelOpen(false)}>
              Keep account
            </button>
            <button className="btn-danger" disabled={deleting} onClick={() => void deleteAccount()}>
              {deleting ? 'Deleting…' : 'Yes, delete everything'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatTile({ value, label, tint, line, fg }: { value: string; label: string; tint: string; line: string; fg: string }) {
  return (
    <div style={{ background: tint, border: `1px solid ${line}`, borderRadius: 18, padding: '20px 22px' }}>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 34, fontWeight: 600, color: fg, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13.5, color: 'var(--ink2)', marginTop: 7 }}>{label}</div>
    </div>
  );
}

const HEAT_LEVELS = ['var(--surface2)', '#CDEBDA', '#8FD9B0', '#4EC286', '#2FBF6D'];

/** 12 Monday-aligned week columns x 7 day rows (Mon..Sun). */
function StudyHeat({ weeks }: { weeks: HeatCell[][] }) {
  let prevMonth = '';
  const labels = weeks.map(cols => {
    const first = cols.find(c => c.key)?.key;
    if (!first) return '';
    const m = new Date(first + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    if (m !== prevMonth) {
      prevMonth = m;
      return m;
    }
    return '';
  });
  return (
    <>
      <div style={{ display: 'flex', gap: 4 }}>
        {weeks.map((cols, ci) => (
          <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {cols.map((c, ri) => (
              <div
                key={ri}
                title={c.key ? `${c.key} · ${c.seconds > 0 ? fmtHm(c.seconds) : 'no study time'}` : '—'}
                style={{ width: 15, height: 15, borderRadius: 4, background: c.key ? HEAT_LEVELS[c.level] : 'transparent' }}
              />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 8, marginBottom: 4 }}>
        {labels.map((m, i) => (
          <span key={i} style={{ width: 15, fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--ink3)', whiteSpace: 'nowrap', overflow: 'visible' }}>
            {m}
          </span>
        ))}
      </div>
    </>
  );
}
